import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Header from './Header'
import Footer from './Footer'
import { useBlog } from '../contexts/BlogContext'
import { BLOG_CONFIG } from '../config/blog'

function Blog() {
  const [posts, setPosts] = useState([])
  const [recentPosts, setRecentPosts] = useState([]) // Separate state for recent posts
  const [error, setError] = useState(null)
  const [localLoading, setLocalLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [cursors, setCursors] = useState([null]) // Array of cursors for each page
  const [searchParams] = useSearchParams()
  const selectedCategoryId = searchParams.get('category') ? Number(searchParams.get('category')) : null
  const { fetchPostsPage, getCursorForPage, isPageCached, getCategoriesList } = useBlog()
  const [categories, setCategories] = useState([])

  // Reset pagination when category changes
  useEffect(() => {
    setCurrentPage(0)
    setCursors([null])
    setRecentPosts([]) // Clear recent posts when category changes
  }, [selectedCategoryId])

  const loadPage = useCallback(async (pageIndex, cursor = null) => {
    try {
      setLocalLoading(true)
      setError(null)

      // Start all API calls in parallel
      const postsPromise = fetchPostsPage(selectedCategoryId, pageIndex, cursor)
      const categoriesPromise = getCategoriesList()
      
      // For initial load, also get recent posts in parallel
      let recentPostsPromise = null
      if (pageIndex === 0) {
        recentPostsPromise = fetchPostsPage(null, 0, null) // All categories, page 0
      }

      // Wait for all promises to complete in parallel
      const results = await Promise.all([
        postsPromise,
        categoriesPromise,
        ...(recentPostsPromise ? [recentPostsPromise] : [])
      ])

      const [postsResult, categories, recentPostsResult] = results
      
      // Set posts data
      setPosts(postsResult.posts)
      
      // Client-side safeguard: if we got fewer posts than the limit, there are no more
      const actualHasMore = postsResult.hasMore && postsResult.posts.length >= BLOG_CONFIG.POSTS_PER_PAGE
      setHasMore(actualHasMore)

      // Store cursor for next page if we have more
      if (postsResult.hasMore && postsResult.nextCursor) {
        setCursors(prev => {
          const newCursors = [...prev]
          newCursors[pageIndex + 1] = postsResult.nextCursor
          return newCursors
        })
      }

      // Set categories
      setCategories(categories)
      
      // Set recent posts only on initial load
      if (pageIndex === 0 && recentPostsResult) {
        setRecentPosts(recentPostsResult.posts.slice(0, BLOG_CONFIG.RECENT_POSTS_COUNT))
      }
    } catch (err) {
      console.error('Error loading posts:', err)
      setError('Error al cargar los artículos. Por favor, intente más tarde.')
    } finally {
      setLocalLoading(false)
    }
  }, [fetchPostsPage, selectedCategoryId, getCategoriesList])

  // Load initial page or when category changes
  useEffect(() => {
    loadPage(0, null)
  }, [selectedCategoryId]) // eslint-disable-line react-hooks/exhaustive-deps

  const goToNextPage = () => {
    if (hasMore) {
      const nextPage = currentPage + 1
      const cursor = cursors[nextPage] || null
      setCurrentPage(nextPage)
      loadPage(nextPage, cursor)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const goToPrevPage = () => {
    if (currentPage > 0) {
      const prevPage = currentPage - 1
      const cursor = cursors[prevPage] || null
      setCurrentPage(prevPage)
      loadPage(prevPage, cursor)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  return (
    <>
      <Header />

      <main id="main">
        <section id="breadcrumbs" className="breadcrumbs">
          <div className="container">
            <ol>
              <li><Link to="/">Inicio</Link></li>
              <li>Blog</li>
              {selectedCategoryId && categories.find(c => c.id === selectedCategoryId) && (
                <li>{categories.find(c => c.id === selectedCategoryId).name}</li>
              )}
            </ol>

            <div className="row blog-content" style={{ marginTop: '40px' }}>
              {/* Sidebar */}
              <div className="col-lg-4">
                <div className="sidebar">
                  <h3 className="sidebar-title">Categorías</h3>
                  <div className="sidebar-item categories">
                    <ul>
                      <li>
                        <Link
                          to="/blog"
                          className={!selectedCategoryId ? 'active' : ''}
                        >
                          Todas las categorías
                        </Link>
                      </li>
                      {categories.map(category => (
                        <li key={category.id}>
                          <Link
                            to={`/blog?category=${category.id}`}
                            className={selectedCategoryId === category.id ? 'active' : ''}
                          >
                            {category.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <h3 className="sidebar-title">Artículos Recientes</h3>
                  <div className="sidebar-item recent-posts">
                    {recentPosts.map(post => (
                      <div key={post.id} className="post-item clearfix">
                        <h4><Link to={`/blog/${post.id}`}>{post.title}</Link></h4>
                        <time dateTime={post.date}>
                          {new Date(post.date).toLocaleDateString('es-ES')}
                        </time>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Blog Posts */}
              <div className="col-lg-8">
                {localLoading ? (
                  <div className="text-center py-5">
                    <p>Cargando artículos...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-5">
                    <p className="text-danger">{error}</p>
                  </div>
                ) : posts.length === 0 ? (
                  <div className="text-center py-5">
                    <p>No hay artículos disponibles.</p>
                  </div>
                ) : (
                  <>
                    {posts.map(post => (
                      <article key={post.id} className="entry" data-aos="fade-up">
                        <div className="entry-meta">
                          <ul>
                            <li className="d-flex align-items-center">
                              <i className="bi bi-person"></i>
                              <span>{post.author}</span>
                            </li>
                            <li className="d-flex align-items-center">
                              <i className="bi bi-clock"></i>
                              <time dateTime={post.date}>
                                {new Date(post.date).toLocaleDateString('es-ES', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </time>
                            </li>
                            <li className="d-flex align-items-center">
                              <i className="bi bi-folder"></i>
                              <span>{post.category}</span>
                            </li>
                          </ul>
                        </div>

                        <h2 className="entry-title">
                          <Link to={`/blog/${post.id}`}>{post.title}</Link>
                        </h2>

                        <div className="entry-content">
                          <p>{post.excerpt}</p>
                          <div className="read-more">
                            <Link to={`/blog/${post.id}`}>Leer más</Link>
                          </div>
                        </div>
                      </article>
                    ))}

                    {/* Pagination */}
                    {(currentPage > 0 || hasMore) && (
                      <div className="blog-pagination">
                        <ul className="justify-content-center">
                          {currentPage > 0 && (
                            <li>
                              <a href="#" onClick={(e) => { e.preventDefault(); goToPrevPage(); }}>
                                ← Anterior
                              </a>
                            </li>
                          )}
                          <li className="active">
                            <span>Página {currentPage + 1}</span>
                          </li>
                          {hasMore && (
                            <li>
                              <a href="#" onClick={(e) => { e.preventDefault(); goToNextPage(); }}>
                                Siguiente →
                              </a>
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  )
}

export default Blog
